import React, { useState, useEffect } from 'react';

const ChatTabBar = ({
  activeChats,
  activeTabIndex,
  onTabSwitch,
  onMinimizeAll,
  onCloseDM,
  onShowHistory
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [tabOffset, setTabOffset] = useState(0);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const maxVisibleTabs = isMobile ? 1 : 3;
  const hasOverflow = activeChats.length > maxVisibleTabs;
  const totalUnreadCount = activeChats.reduce((total, chat) => total + (chat.unreadCount || 0), 0);
  const activeChat = activeChats[activeTabIndex];
  
  // Ensure active tab is visible
  useEffect(() => {
    if (hasOverflow) {
      setTabOffset(prev => {
        if (activeTabIndex < prev) return activeTabIndex;
        if (activeTabIndex >= prev + maxVisibleTabs) {
          return Math.max(0, activeTabIndex - maxVisibleTabs + 1);
        }
        return prev;
      });
    } else {
      setTabOffset(0);
    }
  }, [activeTabIndex, hasOverflow, maxVisibleTabs]);
  
  // Reset offset when chats change
  useEffect(() => {
    if (activeChats.length <= maxVisibleTabs) {
      setTabOffset(0);
    } else if (tabOffset >= activeChats.length - maxVisibleTabs) {
      setTabOffset(Math.max(0, activeChats.length - maxVisibleTabs));
    }
  }, [activeChats.length, maxVisibleTabs, tabOffset]);
  
  const canScrollLeft = tabOffset > 0;
  const canScrollRight = tabOffset + maxVisibleTabs < activeChats.length;
  
  const scrollLeft = () => {
    setTabOffset(Math.max(0, tabOffset - 1));
  };
  
  const scrollRight = () => {
    setTabOffset(Math.min(activeChats.length - maxVisibleTabs, tabOffset + 1));
  };

  if (isMobile) {
    // Mobile: Show single active chat tab
    return (
      <div className="backdrop-blur-sm bg-gray-900/80 dark:bg-black/80 border-b border-gray-700 dark:border-gray-800">
        <div className="flex items-center justify-between px-3 py-2">
          {/* Active Chat Info */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Chat Icon */}
            {activeChat?.type === 'global' ? (
              <svg className="w-5 h-5 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : activeChat?.otherAvatarUrl ? (
              <img 
                src={activeChat.otherAvatarUrl} 
                alt={activeChat.otherDisplayName || activeChat.otherUsername}
                className="w-6 h-6 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <svg className="w-5 h-5 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            )}
            
            {/* Chat Title */}
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-white font-medium text-sm truncate">
                {activeChat?.title || 'Chat'}
              </span>
              {activeChats.length > 1 && (
                <span className="text-blue-200 text-xs">
                  {activeTabIndex + 1} of {activeChats.length}
                </span>
              )}
            </div>
            
            {/* Total Unread Count */}
            {totalUnreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold">
                {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
              </span>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Switch Chat Button (if multiple chats) */}
            {activeChats.length > 1 && (
              <button
                onClick={() => {
                  const nextIndex = (activeTabIndex + 1) % activeChats.length;
                  onTabSwitch(nextIndex);
                }}
                className="p-1.5 text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white transition-colors rounded"
                title="Switch chat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </button>
            )}
            
            {/* Chat Management Button */}
            {onShowHistory && (
              <button
                onClick={onShowHistory}
                className="p-1.5 bg-gray-700 dark:bg-gray-800 text-white hover:bg-gray-600 dark:hover:bg-gray-700 rounded transition-colors"
                title="Chat management"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </button>
            )}
            
            {/* Minimize Button */}
            <button
              onClick={onMinimizeAll}
              className="p-1.5 text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white transition-colors rounded"
              title="Minimize chat"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop: Separate tab area and action buttons
  return (
    <div className="backdrop-blur-sm bg-gray-900/80 dark:bg-black/80 border-b border-gray-700 dark:border-gray-800 md:rounded-t-lg relative h-12">
      
      {/* Tab Area Container - Takes full width minus action button space */}
      <div 
        className="absolute left-0 top-0 bottom-0 flex items-center"
        style={{ right: '80px' }} // Hard stop at 80px from right edge (20 * 4 = 80)
      >
        {/* Left Navigation Arrow */}
        {hasOverflow && canScrollLeft && (
          <button
            onClick={scrollLeft}
            className="flex-shrink-0 p-1.5 text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white transition-colors border-r border-gray-700 dark:border-gray-800"
            title="Scroll left"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        
        {/* Tabs Container - Takes remaining space */}
        <div className="flex-1 overflow-hidden">
          <div className="flex h-full">
            {activeChats.slice(tabOffset, tabOffset + maxVisibleTabs).map((chat, visibleIndex) => {
              const actualIndex = tabOffset + visibleIndex;
              return (
                <div
                  key={chat.id}
                  className={`relative flex items-center gap-1 md:gap-2 px-3 py-3 text-xs md:text-sm font-medium transition-colors border-r border-gray-700 dark:border-gray-800 flex-1 cursor-pointer ${
                    actualIndex === activeTabIndex
                      ? 'text-white'
                      : 'bg-gray-800 dark:bg-gray-900 text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800'
                  }`}
                  style={actualIndex === activeTabIndex ? { backgroundColor: '#38c494' } : {}}
                  title={chat.title}
                  onClick={() => onTabSwitch(actualIndex)}
                >
                  {/* Chat Type Icon */}
                  {chat.type === 'global' ? (
                    <svg className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                  
                  {/* Chat Title */}
                  <span className="truncate">
                    {chat.type === 'global' ? 'Global' : chat.title}
                  </span>
                  
                  {/* Unread Count */}
                  {chat.unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-[10px] md:text-xs rounded-full min-w-[14px] md:min-w-[16px] h-3 md:h-4 flex items-center justify-center font-bold ml-0.5 md:ml-1 flex-shrink-0">
                      {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                    </span>
                  )}
                  
                  {/* Close Button for DMs */}
                  {chat.type === 'dm' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCloseDM(chat.id);
                      }}
                      className="ml-0.5 md:ml-1 p-0.5 rounded hover:bg-red-500 transition-colors flex-shrink-0"
                      title="Close conversation"
                    >
                      <svg className="w-2.5 h-2.5 md:w-3 md:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Right Navigation Arrow */}
        {hasOverflow && canScrollRight && (
          <button
            onClick={scrollRight}
            className="flex-shrink-0 p-1.5 text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white transition-colors border-l border-gray-700 dark:border-gray-800"
            title="Scroll right"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
      
      {/* Action Buttons - Completely separate, fixed position */}
      <div className="absolute right-0 top-0 bottom-0 w-20 flex items-center backdrop-blur-sm bg-gray-900/80 dark:bg-black/80 border-l border-gray-700 dark:border-gray-800 z-10">
        {/* Chat Management Button */}
        {onShowHistory && (
          <>
            <button
              onClick={onShowHistory}
              className="flex-1 h-full flex items-center justify-center p-1.5 text-white hover:opacity-80 transition-colors"
              style={{ backgroundColor: '#1d212e' }}
              title="Chat management"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
            
            {/* Separator */}
            <div className="w-px h-6 bg-gray-600 dark:bg-gray-500"></div>
          </>
        )}
        
        {/* Minimize All Button */}
        <button
          onClick={onMinimizeAll}
          className="flex-1 h-full flex items-center justify-center p-1.5 text-white hover:opacity-80 transition-colors"
          style={{ backgroundColor: '#1d212e' }}
          title="Minimize all chats"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatTabBar;